/**
 * In-memory cache of the user's own account-level X25519 keypair (Phase 3).
 *
 * Single-value (not per-orgId — every user has exactly one account keypair).
 * Populated by SafeContext after OPAQUE login or local unlock; the publicKey
 * is also published to the server on UserRecord. Cleared on logout/lockVault.
 *
 * The promote-to-admin flow uses publicKey to look up another user's pubkey
 * (via the GET /orgs/:id member-list response, not from this context — this
 * context only holds the CURRENT user's own keypair). The finalize-promotion
 * flow uses privateKey to decrypt the pending bundle.
 */

import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

interface AccountKeyContextValue {
  publicKey: Uint8Array | null;
  privateKey: Uint8Array | null;
  /**
   * True when SafeContext attempted to recover the account keypair (on login or
   * unlock) but the stored wrap could not be opened — a persistent condition
   * (usually a stale local wrap after a password change), NOT the brief
   * still-loading race. Consumers use it to prompt "sign in again to finish
   * setup" instead of failing silently and later surfacing confusing downstream
   * errors ("Finishing account setup", "unable to decrypt" categories).
   */
  recoveryFailed: boolean;
  setKeyPair(publicKey: Uint8Array, privateKey: Uint8Array): void;
  /** Signal that account-key recovery was attempted and failed. */
  markRecoveryFailed(): void;
  clear(): void;
}

const AccountKeyContext = createContext<AccountKeyContextValue | null>(null);

export function AccountKeyProvider({ children }: { children: ReactNode }) {
  // Bump a version state when contents change so consumers re-render.
  // The actual keys live in refs so updates don't leak intermediate state.
  const pubRef = useRef<Uint8Array | null>(null);
  const privRef = useRef<Uint8Array | null>(null);
  const recoveryFailedRef = useRef<boolean>(false);
  const [, setVersion] = useState(0);

  const setKeyPair = useCallback((publicKey: Uint8Array, privateKey: Uint8Array) => {
    pubRef.current = publicKey;
    privRef.current = privateKey;
    recoveryFailedRef.current = false; // a successful recovery clears any prior failure
    setVersion((v) => v + 1);
  }, []);

  const markRecoveryFailed = useCallback(() => {
    recoveryFailedRef.current = true;
    setVersion((v) => v + 1);
  }, []);

  const clear = useCallback(() => {
    pubRef.current = null;
    privRef.current = null;
    recoveryFailedRef.current = false;
    setVersion((v) => v + 1);
  }, []);

  const value = useMemo<AccountKeyContextValue>(
    () => ({
      get publicKey() { return pubRef.current; },
      get privateKey() { return privRef.current; },
      get recoveryFailed() { return recoveryFailedRef.current; },
      setKeyPair,
      markRecoveryFailed,
      clear,
    }),
    [setKeyPair, markRecoveryFailed, clear],
  );

  return <AccountKeyContext.Provider value={value}>{children}</AccountKeyContext.Provider>;
}

export function useAccountKey(): AccountKeyContextValue {
  const ctx = useContext(AccountKeyContext);
  if (!ctx) {
    throw new Error('useAccountKey must be called inside an AccountKeyProvider');
  }
  return ctx;
}
